/**
 * <protector-upsell>
 *
 * Companion component for snippets/protector-upsell.liquid.
 *
 * Responsibilities:
 *  - Listens for the jacket's variant selection changing (radios, selects,
 *    or theme-specific swatches) and resolves the matching protector handle
 *    via the per-product override map first, then the global default map.
 *  - Fetches /products/<handle>.js on demand for any handle not already
 *    embedded in the server-rendered payload, and caches results.
 *  - Updates the visible image, title link, and "Yes, add for ..." price.
 *  - Hides the block (or shows "Currently unavailable") when the matched
 *    protector is out of stock, per the theme setting.
 *  - Intercepts the parent product form's submit when "Yes" is chosen and
 *    POSTs both items in one /cart/add.js call so the user sees a single
 *    cart-drawer update. Falls back to the form's normal handler for "No".
 *
 * Extension points used by other code:
 *  - Listens for `cart:item-added` is unnecessary here; we don't need to
 *    react after the add succeeds, the existing product-form handler runs
 *    its renderContents() flow which we'll piggyback on by calling
 *    document.dispatchEvent('cart:refresh').
 */
if (!customElements.get('protector-upsell')) {
  class ProtectorUpsell extends HTMLElement {
    constructor() {
      super();
      this._handleCache = new Map();
      this._currentMatch = null;
      this._formAttached = false;
    }

    connectedCallback() {
      try {
        this.pairingMode = this.dataset.pairingMode || 'single_product';

        const payloadEl = this.querySelector('[data-protector-payload]');
        const initial = payloadEl ? JSON.parse(payloadEl.textContent) : [];
        // Cache key depends on pairing mode:
        //  - single_product: variant_size (option1 of the protector variant)
        //  - multiple_products: lookup_handle (handle the size_mapping points to)
        initial.forEach((p) => {
          const key = this.pairingMode === 'multiple_products'
            ? (p.lookup_handle || p.handle)
            : p.variant_size;
          if (key) this._handleCache.set(key, p);
        });

        this.defaultKey = this.pairingMode === 'multiple_products'
          ? (this.dataset.defaultHandle || null)
          : (this.dataset.defaultVariantSize || null);
        this.showUnavailableMessage = this.dataset.showUnavailable === 'true';

        this.defaultMap = this._safeJsonParse(this.dataset.defaultMap);
        this.overrideMap = this._safeJsonParse(this.dataset.overrideMap);

        this.form = document.getElementById(this.dataset.productFormId);
        this.variantInput = this.querySelector('[data-protector-variant-input]');
        this.choiceInput = this.querySelector('[data-protector-choice-input]');

        this._wireChoiceRadios();
        this._wireVariantListener();
        this._wireFormInterception();
        this._render();
      } catch (err) {
        console.warn('[protector-upsell] init failed', err);
        this.hidden = true;
      }
    }

    /* ----- helpers ----- */

    _safeJsonParse(raw) {
      if (!raw) return null;
      try {
        const parsed = JSON.parse(raw);
        return (parsed && typeof parsed === 'object') ? parsed : null;
      } catch (e) {
        console.warn('[protector-upsell] invalid JSON map', raw);
        return null;
      }
    }

    /**
     * Looks up the matching cache key for a given jacket size.
     * In single_product mode, the key is a variant size (option1).
     * In multiple_products mode, the key is a product handle.
     * Per-product override > theme default > the paired protector's default
     * (variant size or handle). Supports a "*" wildcard key.
     */
    _resolveKey(jacketSize) {
      const lookup = (map) => {
        if (!map) return null;
        if (jacketSize && Object.prototype.hasOwnProperty.call(map, jacketSize)) return map[jacketSize];
        if (Object.prototype.hasOwnProperty.call(map, '*')) return map['*'];
        return null;
      };
      return lookup(this.overrideMap) || lookup(this.defaultMap) || this.defaultKey;
    }

    /**
     * Returns the jacket's currently-selected size by scanning the variant
     * picker. The theme renders variant inputs OUTSIDE the form (associated
     * via [form="..."]), so this looks at <variant-radios>-style fieldsets
     * across the document. We pick whichever fieldset's checked value
     * actually resolves against the size mapping — that way it doesn't
     * matter whether the size lives in option1 or option2, and the option
     * label can be localized.
     */
    _getCurrentJacketSize() {
      const candidates = this._collectOptionValues();
      if (!candidates.length) return null;

      // Prefer a value that has a direct mapping entry (not the wildcard).
      const mapHasKey = (key) => {
        if (this.overrideMap && Object.prototype.hasOwnProperty.call(this.overrideMap, key)) return true;
        if (this.defaultMap && Object.prototype.hasOwnProperty.call(this.defaultMap, key)) return true;
        return false;
      };
      const direct = candidates.find((v) => mapHasKey(v));
      if (direct) return direct;

      // Otherwise return the value that LOOKS like a clothing size, so the
      // wildcard ("*") fallback still applies. Common patterns: S, M, L, XL,
      // XXL, 3XL, XXS, XS, EU 50, US 10, etc.
      const sizeRe = /^(XX?XS|XS|S|M|L|XL|XXL|3XL|4XL|XXXL)$/i;
      const sized = candidates.find((v) => sizeRe.test(v.trim()));
      if (sized) return sized;

      // Last resort — first value found.
      return candidates[0] || null;
    }

    _collectOptionValues() {
      const values = [];
      // Match the theme's variant fieldsets (snippets/product-option.liquid).
      const fieldsets = document.querySelectorAll('fieldset.product-form__input');
      fieldsets.forEach((fs) => {
        // Skip color/swatch fieldsets — they don't hold a size value.
        if (fs.classList.contains('product-form__input--color')) return;
        const select = fs.querySelector('select');
        if (select && select.value) { values.push(select.value); return; }
        const checked = fs.querySelector('input[type="radio"]:checked');
        if (checked && checked.value) values.push(checked.value);
      });
      return values;
    }

    /* ----- listeners ----- */

    _wireChoiceRadios() {
      this.querySelectorAll('[data-protector-choice]').forEach((radio) => {
        radio.addEventListener('change', () => {
          if (this.choiceInput) this.choiceInput.value = radio.value;
        });
      });
    }

    _wireVariantListener() {
      // The Pando theme dispatches `product:variant-change` on the document
      // whenever the size/color radios or selects change. The variant inputs
      // live OUTSIDE the product form (associated via [form="…"]), so a
      // form-scoped 'change' listener doesn't catch them — we listen on the
      // document instead. We also bind to `change` on the document as a
      // belt-and-braces fallback for variant components that don't dispatch
      // the custom event.
      this._boundVariantHandler = this._onVariantChange.bind(this);
      document.addEventListener('product:variant-change', this._boundVariantHandler);
      document.addEventListener('variant:change', this._boundVariantHandler);
      document.addEventListener('change', (e) => {
        const t = e.target;
        if (t && t.matches && t.matches('fieldset.product-form__input input[type="radio"], fieldset.product-form__input select')) {
          this._onVariantChange(e);
        }
      });
    }

    _onVariantChange() {
      // Defer to next tick so the form value reflects the new state.
      setTimeout(() => this._render(), 0);
    }

    _wireFormInterception() {
      if (!this.form || this._formAttached) return;
      this._formAttached = true;
      // Capture phase so we can preempt <product-form>'s submit handler when
      // YES is selected. We stop propagation in that branch so it doesn't
      // also fire its single-item add.
      this.form.addEventListener('submit', this._onFormSubmit.bind(this), { capture: true });
    }

    /**
     * Companion contract (see euro-bundle.js): lets another upsell component
     * intercepting the same form carry the protector in its submission.
     */
    getCompanionItem() {
      const yesChecked = this.querySelector('[data-protector-choice="yes"]')?.checked;
      if (!yesChecked) return null;
      if (!this._currentMatch || !this._currentMatch.available) return null;
      return {
        item: { id: this._currentMatch.variant_id, quantity: 1, properties: { _added_via: 'upsell' } },
        onAdded: null,
      };
    }

    async _onFormSubmit(event) {
      if (event._pandoCompanionsHandled) return; // another upsell already carries us
      const yesChecked = this.querySelector('[data-protector-choice="yes"]')?.checked;
      if (!yesChecked) return; // let normal handler run

      if (!this._currentMatch || !this._currentMatch.available) return; // safety

      event._pandoCompanionsHandled = true;
      event.preventDefault();
      event.stopImmediatePropagation();

      const formData = new FormData(this.form);
      const jacketVariantId = formData.get('id');
      const jacketQty = parseInt(formData.get('quantity'), 10) || 1;
      if (!jacketVariantId) return;

      const sectionsToRender = ['cart-drawer', 'cart-bubble', 'main-cart'];

      const items = [
        {
          id: Number(jacketVariantId),
          quantity: jacketQty,
        },
        {
          id: this._currentMatch.variant_id,
          quantity: 1,
          properties: { _added_via: 'upsell' },
        },
      ];

      // Forward any other line-item properties from the form (e.g. gift card
      // recipient fields) onto the jacket item.
      const props = {};
      for (const [key, value] of formData.entries()) {
        const m = /^properties\[(.+)\]$/.exec(key);
        if (m) props[m[1]] = value;
      }
      if (Object.keys(props).length) items[0].properties = props;

      // Collect companions from other upsell components on the same form
      // (e.g. the €1 gift) so a single submission carries everything.
      const companionCallbacks = [];
      document.querySelectorAll('euro-bundle').forEach((el) => {
        if (el.form !== this.form || typeof el.getCompanionItem !== 'function') return;
        const c = el.getCompanionItem();
        if (c && c.item) {
          items.push(c.item);
          if (c.mainProperties) {
            items[0].properties = Object.assign(items[0].properties || {}, c.mainProperties);
          }
          if (c.onAdded) companionCallbacks.push(c.onAdded);
        }
      });
      this._companionCallbacks = companionCallbacks;

      this._setSubmittingState(true);

      try {
        const response = await fetch(`${window.theme.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items, sections: sectionsToRender, sections_url: window.location.pathname }),
        });
        const data = await response.json();
        if (data.status && data.status >= 400) {
          console.warn('[protector-upsell] cart/add failed', data);
          this._setSubmittingState(false);
          // Fall back to the normal single-item add.
          this.form.submit();
          return;
        }
        this._renderCartContents(data);
        (this._companionCallbacks || []).forEach((cb) => { try { cb(); } catch (e) {} });
        this._companionCallbacks = null;
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: data.items?.[0] || data } }));
      } catch (err) {
        console.error('[protector-upsell] cart add error', err);
        // Fall back to native form submission so the user isn't stuck.
        this.form.submit();
      } finally {
        this._setSubmittingState(false);
      }
    }

    _setSubmittingState(isSubmitting) {
      const btns = document.querySelectorAll('.single-add-to-cart-button');
      btns.forEach((btn) => {
        btn.classList.toggle('loading', isSubmitting);
        btn.toggleAttribute('aria-disabled', isSubmitting);
      });
    }

    /**
     * Mirrors the renderContents() pattern used by <product-form> so the
     * cart drawer + bubble update inline.
     */
    _renderCartContents(parsedState) {
      const sections = parsedState.sections || {};
      const targets = [
        { id: 'Cart', selector: '.thb-cart-form', section: 'main-cart' },
        { id: 'Cart-Drawer', selector: '.cart-drawer', section: 'cart-drawer' },
        { id: 'cart-drawer-toggle', selector: '#cart-drawer-toggle', section: 'cart-bubble' },
      ];
      targets.forEach((t) => {
        const host = document.getElementById(t.id);
        if (!host || !sections[t.section]) return;
        const replace = host.querySelector(t.selector) || host;
        try {
          const html = new DOMParser().parseFromString(sections[t.section], 'text/html');
          const inner = html.querySelector(t.selector);
          if (inner) replace.innerHTML = inner.innerHTML;
        } catch (e) {
          console.warn('[protector-upsell] failed to render', t.section, e);
        }
      });
      const drawer = document.getElementById('Cart-Drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      // Re-bind cart events after innerHTML swap (matches product-form behaviour).
      drawer?.notesToggle?.();
      drawer?.removeProductEvent?.();
    }

    /* ----- rendering ----- */

    _render() {
      const jacketSize = this._getCurrentJacketSize();
      const key = this._resolveKey(jacketSize);

      if (!key) {
        this._showHidden(true);
        this._currentMatch = null;
        if (this.variantInput) this.variantInput.value = '';
        return;
      }

      const payload = this._handleCache.get(key);
      if (!payload) {
        const what = this.pairingMode === 'multiple_products' ? 'protector handle' : 'protector variant size';
        console.warn('[protector-upsell] no payload for ' + what, key, '— hiding block. Check the mapping and that the referenced product/variant exists.');
        this._showHidden(true);
        this._currentMatch = null;
        if (this.variantInput) this.variantInput.value = '';
        return;
      }

      this._currentMatch = payload;
      this._paint(payload);
    }

    _formatMoney(cents) {
      if (typeof window.Shopify?.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
      }
      return (cents / 100).toFixed(2);
    }

    _paint(payload) {
      const isAvailable = !!payload.available;

      // Show / hide whole block per OOS rule
      if (!isAvailable && !this.showUnavailableMessage) {
        this._showHidden(true);
        if (this.variantInput) this.variantInput.value = '';
        return;
      }

      this._showHidden(false);
      this.classList.toggle('is-unavailable', !isAvailable);

      // Image
      const img = this.querySelector('[data-protector-image]');
      if (img && payload.image) {
        img.src = payload.image;
        img.alt = payload.image_alt || payload.title || '';
        img.removeAttribute('srcset');
      }
      const imgLink = this.querySelector('[data-protector-image-link]');
      if (imgLink && payload.url) imgLink.href = payload.url;

      // Title link — mirrors the product-information.liquid title split
      // (normalize en-dash, then split on " - " and keep only the first
      // segment, e.g. "QUATROFLEX BACK PROTECTOR" instead of "QUATROFLEX
      // BACK PROTECTOR - CE Approved for XL-XXXL").
      const titleLink = this.querySelector('[data-protector-link]');
      if (titleLink) {
        if (payload.url) titleLink.href = payload.url;
        titleLink.textContent = this._fillTemplate(
          titleLink.dataset.template,
          'Include __product__',
          { product: this._shortTitle(payload.title) }
        );
      }

      // Yes-radio price label
      const yesLabel = this.querySelector('[data-protector-yes-label]');
      if (yesLabel) {
        yesLabel.textContent = this._fillTemplate(
          yesLabel.dataset.template,
          'Yes, add for __price__',
          { price: payload.price_html }
        );
      }

      // OOS message
      const oosEl = this.querySelector('[data-protector-unavailable]');
      if (oosEl) oosEl.hidden = isAvailable;

      // Hidden form input
      if (this.variantInput) this.variantInput.value = isAvailable ? payload.variant_id : '';

      // If unavailable, force "no" choice and disable the "yes" radio.
      if (!isAvailable) {
        const yes = this.querySelector('[data-protector-choice="yes"]');
        const no = this.querySelector('[data-protector-choice="no"]');
        if (yes) { yes.checked = false; yes.disabled = true; }
        if (no) no.checked = true;
        if (this.choiceInput) this.choiceInput.value = 'no';
      } else {
        const yes = this.querySelector('[data-protector-choice="yes"]');
        if (yes) yes.disabled = false;
      }
    }

    _showHidden(isHidden) {
      this.hidden = isHidden;
    }

    _formatString(template, fallback, vars) {
      const tpl = template || fallback;
      return tpl.replace(/{{\s*(\w+)\s*}}/g, (_, key) => (vars[key] !== undefined ? vars[key] : ''));
    }

    /**
     * Same as _formatString but uses `__key__` placeholder tokens. The
     * tokens are emitted server-side by the Liquid snippet (Liquid can't
     * pass a literal `{{ price }}` as a t-filter argument without
     * confusing its parser, so we use a non-Liquid placeholder).
     */
    _fillTemplate(template, fallback, vars) {
      const tpl = template || fallback;
      return tpl.replace(/__(\w+)__/g, (m, key) => (vars[key] !== undefined ? vars[key] : m));
    }

    _shortTitle(title) {
      if (!title) return '';
      // Normalise the en-dash to an ASCII hyphen so the split below catches
      // either separator, then keep just the first segment.
      const normalized = String(title).replace(/ – /g, ' - ');
      return normalized.split(' - ')[0];
    }
  }

  customElements.define('protector-upsell', ProtectorUpsell);
}